<template>
  <div class="features-page">
    <!-- Hero -->
    <section class="features-hero">
      <div class="container">
        <div class="row justify-content-center text-center">
          <div class="col-lg-9">
            <p class="eyebrow">What runs the engine</p>
            <h1 class="hero-title">Every part of your venue,<br class="d-none d-md-inline" /> on one engine.</h1>
            <p class="hero-sub">
              Every module is included in the base plan. Jump to any one below,
              or see how they fit together on the
              <a href="/#journey" class="hero-sub-link">customer journey</a>.
            </p>
          </div>
        </div>

        <nav class="features-jump-nav" aria-label="Jump to feature">
          <a
            v-for="feature in orderedFeatures"
            :key="feature.key"
            :href="'#' + feature.key"
            class="jump-chip"
            @click.prevent="goToAnchor(feature.key)"
          >
            <svg-icon :name="feature.icon" :size="15" />
            <span>{{ feature.name }}</span>
          </a>
        </nav>
      </div>
    </section>

    <!-- Chapters: related modules share a surface -->
    <div
      v-for="chapter in chapterView"
      :key="chapter.title"
      :class="['feature-chapter', { 'section-band': chapter.band }]"
    >
      <div class="container chapter-head">
        <p class="chapter-eyebrow">{{ chapter.eyebrow }}</p>
        <h2 class="chapter-title">{{ chapter.title }}</h2>
      </div>

      <section
        v-for="feature in chapter.modules"
        :key="feature.key"
        :id="feature.key"
        class="feature-section"
      >
      <div class="container">
        <div class="feature-header">
          <div class="feature-icon-wrap">
            <svg-icon :name="feature.icon" :size="28" />
          </div>
          <h2 class="feature-section-title">{{ feature.name }}</h2>
          <p class="feature-section-body">{{ feature.intro }}</p>
        </div>

        <div class="row align-items-start feature-row">
          <div class="col-md-5 mb-4 mb-md-0">
            <ul class="feature-bullet-list">
              <li
                v-for="bullet in interactiveBullets(feature)"
                :key="bullet.label"
                :class="['feature-bullet', { active: activeBulletLabel(feature) === bullet.label }]"
                @click="setActiveByLabel(feature.key, bullet.label)"
                @mouseenter="setActiveByLabel(feature.key, bullet.label)"
                @focus="setActiveByLabel(feature.key, bullet.label)"
                @keydown.enter.prevent="setActiveByLabel(feature.key, bullet.label)"
                @keydown.space.prevent="setActiveByLabel(feature.key, bullet.label)"
                role="button"
                tabindex="0"
                :aria-pressed="activeBulletLabel(feature) === bullet.label"
              >
                <svg-icon name="check" :size="16" />
                <span>{{ bullet.label }}</span>
                <span v-if="bullet.soon" class="soon-pill">Coming soon</span>
              </li>
            </ul>

            <div v-if="alsoIncludes(feature).length" class="feature-also-includes">
              <h4>Also includes</h4>
              <ul>
                <li v-for="bullet in alsoIncludes(feature)" :key="bullet.label">
                  <svg-icon name="check" :size="14" />
                  <span>{{ bullet.label }}</span>
                </li>
              </ul>
            </div>
          </div>

          <div class="col-md-7">
            <div class="screenshot-stage">
              <transition name="screenshot-fade" mode="out-in">
                <img
                  v-if="currentImage(feature)"
                  :key="currentImage(feature)"
                  :src="currentImage(feature)"
                  :alt="currentBullet(feature).label"
                  class="screenshot-image"
                  loading="lazy"
                />
              </transition>
              <div v-if="currentBullet(feature)" class="screenshot-caption">
                <p class="screenshot-caption-title">{{ currentBullet(feature).label }}<span v-if="currentBullet(feature).soon" class="soon-pill">Coming soon</span></p>
                <p v-if="currentBullet(feature).blurb" class="screenshot-caption-blurb">
                  {{ currentBullet(feature).blurb }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      </section>
    </div>

    <!-- End-of-page CTA band -->
    <section class="cta-band">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-8">
            <h2 class="cta-band-title">The whole engine, unlocked from day one.</h2>
            <p class="cta-band-sub">
              One plan, every module included. $50 a month minimum, then pay-per-use
              rates for what your venue actually does.
            </p>
            <div class="cta-band-buttons">
              <a href="/contact" class="btn-cta-primary">Get a demo</a>
              <a href="/pricing" class="btn-cta-secondary">See pricing &rarr;</a>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
const S = '/img/shots';

export default {
  name: "Features",
  data() {
    return {
      features: [
        {
          key: 'bookings',
          name: 'Booking & resource management',
          icon: 'calendar',
          tagline: 'More bookable inventory, no double bookings',
          intro:
            "Give customers more times to choose from without worrying about conflicts. OTC automatically manages availability around your rooms, staff, equipment, and other resources, blocking and opening booking times as availability changes. The result is more bookable inventory, more revenue opportunities, and no double bookings. Manage it all from list, calendar, resource calendar, or kanban views, with real-time updates across your entire team.",
          bullets: [
            { label: 'Automatic resource-based blocking and availability', image: `${S}/bk-resource-scheduling.png`, blurb: 'Availability is calculated from your actual resources. When a room, host, or piece of equipment gets committed, every booking time that depended on it closes itself, and reopens the moment the conflict clears.' },
            { label: 'Maximize bookable times without double booking',    image: `${S}/landing-bookings.png`, blurb: 'Because the system enforces the conflicts, you can safely publish far more start times than a manual schedule would allow. More bookable inventory means more revenue from the same operating hours.' },
            { label: 'Request to book with approval before charging', image: `${S}/bk-requests.png`, blurb: 'Customers request a time and you approve or decline each request. The card is charged only when you approve, so buyouts and off-hours bookings never surprise you.' },
            { label: 'Public time slots with per-ticket pricing', image: `${S}/bk-public-tickets.png`, blurb: 'Sell individual tickets into a shared slot, priced by Adult, Child, or any category you define. Seats hold while guests check out, and a group can upgrade to a private booking.' },
            { label: 'One event sold at several lengths and prices', image: `${S}/bk-durations.png`, blurb: 'Sell one event at several lengths, each with its own pricing, so groups book the experience that fits their time and budget.' },
            { label: 'Manage rooms, staff, equipment, and other resources', image: `${S}/bk-resources.png`, blurb: 'Model every dependency your experiences rely on, from rooms and hosts to headsets, axes, and vehicles. Each one gets its own lane on the resource calendar, assigned automatically as bookings land.' },
            { label: 'Four views with real-time updates across your team', image: `${S}/module-bookings.png`, blurb: 'The same booking data rendered as a filterable list, a day calendar, a resource calendar, and a workflow board. Edits sync across every open screen instantly, so the front desk and the back office never disagree.' },
            { label: 'Drag-and-drop booking workflows',                   image: `${S}/bk-kanban-workflow.png`, blurb: 'Bookings move through your operational stages as cards: checked in, event completed, photos sent, upsell made. Drag to update, and nothing falls through the cracks between arrival and follow-up.' },
            { label: 'Workflow cards with staff, payments, and notes',    image: `${S}/bk-kanban-cards.png`, blurb: 'Each card shows the event, group size, time, assigned staff, amount paid, and notes, so the board answers most questions without opening a booking.' },
            { label: 'Recurring schedules per event and location',        image: `${S}/bk-schedules.png`, blurb: 'Define recurring weekly schedules per event and location. Adjust hours seasonally without touching individual slots; the calendar regenerates itself.' },
            { label: 'On-demand custom slots outside the schedule',       image: `${S}/bk-custom-slot.png`, blurb: 'Create a one-off slot at any time outside the regular schedule: a late-night corporate group, a press event, a special request. It behaves like any other booking from then on.' },
            { label: 'Bulk booking and schedule management',              image: `${S}/bk-bulk.png`, blurb: 'Apply changes across a whole date range at once: close slots, move bookings, adjust schedules. What used to be an evening of clicking is one operation.' },
            { label: 'Closed periods that auto-block conflicting slots',  image: `${S}/bk-closed-periods.png`, blurb: 'Mark a holiday or maintenance period and every conflicting slot blocks itself, with affected bookings surfaced so you can rebook them.' },
            { label: 'Event log and full audit trail',                    image: `${S}/bk-event-log.png`, blurb: 'Every booking carries its complete history: who changed what, when payments landed, which emails went out. When a customer disputes something, the answer is a lookup, not a debate.' },
          ],
        },
        {
          key: 'booking-site',
          name: 'Branded booking site',
          icon: 'layout',
          tagline: 'Customer-facing pages, your way',
          intro:
            "Run multiple booking site templates from one account, each with its own URL, branding, event list, checkout rules, and confirmation experience. Embed on your site or run hosted at your venue's domain.",
          bullets: [
            { label: 'Multiple templates with their own URL slugs and styling', image: `${S}/module-booking-site.png`, blurb: 'Run any number of booking site templates from one account, each with its own URL, event list, and checkout rules. One venue can present differently to corporate clients and birthday parties.' },
            { label: 'Brand-matched colors, fonts, and copy',                   image: `${S}/site-branding.png`, blurb: 'Match your site with RGB-exact colors and Google Fonts. Customers never feel like they left your website to book.' },
            { label: 'Personalized booking slot messages',                      image: `${S}/site-slot-messages.png`, blurb: 'Attach a custom message to any slot: an early-arrival note, a parking tip, a discount callout. The right information reaches customers at the moment they pick a time.' },
            { label: 'Embeddable on your existing website',                     image: `${S}/site-embed.png`, blurb: 'Embed the entire booking flow into your existing website with a snippet, or run it hosted. Either way, checkout stays on your brand.' },
            { label: 'Booking site in your guests\' language', image: `${S}/site-language.png`, blurb: 'Set a default language for each location and let guests switch on the booking site, with prices shown in your currency.' },
            { label: 'Suggest another event at checkout', image: `${S}/site-upsell.png`, blurb: 'Recommend a second experience in the cart, with an optional promo attached, so one booking turns into two.' },
            { label: 'Reusable custom-field library across every form', image: `${S}/new-custom-fields.png`, blurb: 'Build questions once in a shared library and attach them to any booking site, waiver, or internal form. Update a field in one place and it changes everywhere.' },
            { label: 'Multi-item add-ons and cart support',                     image: `${S}/site-cart.png`, blurb: 'Customers can book an experience, add gift cards or merchandise, and pay for everything in a single checkout.' },
            { label: 'White-labeled confirmation experience with waiver links', image: `${S}/site-confirmation.png`, blurb: 'Confirmations are white-labeled and carry a pre-filled waiver link, so the signing process starts the moment the booking is made.' },
          ],
        },
        {
          key: 'payments',
          name: 'Payments, gift cards & promos',
          icon: 'shopping-cart',
          tagline: 'Gateways, gift cards, promo codes, inventory',
          intro:
            "A complete payment stack for venue operations. Multiple gateway integrations, gift cards (digital and physical), promo codes with eligibility rules, inventory of merchandise and add-ons, configurable taxes and fees, customer credit, and saved cards on file.",
          bullets: [
            { label: 'Major payment gateways supported',                  image: `${S}/pay-gateways.png`, blurb: 'Connect the major payment gateways and keep the processor rates you already negotiated. Payments settle to your accounts, with every transaction recorded in the platform.' },
            { label: 'Custom payment methods you define',                 image: `${S}/pay-methods.png`, blurb: 'Define your own payment methods for cash, external terminals, vouchers, or trade deals, so the books reflect how money actually arrives.' },
            { label: 'Digital and physical gift cards with custom values', image: `${S}/module-payments.png`, blurb: 'Sell digital and physical gift cards with fixed values, customer-chosen amounts, or event pricing. Balances, redemptions, and activations track themselves.' },
            { label: 'Promo codes with eligibility and combinability rules', image: `${S}/pay-promocodes.png`, blurb: 'Build promo codes with eligibility rules, usage limits, and combinability settings. Group related codes to measure a whole campaign at once.' },
            { label: 'Configurable taxes and fees per location',          image: `${S}/pay-taxes-fees.png`, blurb: 'Configure taxes and fees per location and they apply themselves correctly at checkout. Multi-location operators keep one system and many rule sets.' },
            { label: 'Tiered or flat pricing per event',                  image: `${S}/pay-tiered.png`, blurb: 'Price by group size with tiers or keep it flat per event. Larger groups can earn per-person discounts automatically.' },
            { label: 'Dynamic, slot-specific pricing',                    image: `${S}/pay-dynamic.png`, blurb: 'Override the price of any single slot: premium Saturday nights, discounted weekday mornings, or one special event.' },
            { label: 'Deposits for high-value bookings',                  image: `${S}/pay-deposits.png`, blurb: 'Take a deposit at booking and collect the balance on arrival. Large reservations stop being a leap of faith.' },
            { label: 'Payment links to collect any balance', image: null, blurb: 'Send a customer a link to pay what is left on their order, so balances are settled before the group arrives.' },
            { label: 'Card-on-file storage for later charges',            image: `${S}/pay-deposits-b.png`, blurb: 'Cards save securely at checkout for later charges: no-show fees or day-of add-ons, without chasing the customer for a number.' },
          ],
        },
        {
          key: 'pos',
          name: 'Point of sale',
          icon: 'dollar-sign',
          tagline: 'In-person payments on your own devices',
          intro:
            "Take in-person payments on Android phones, tablets, and Stripe smart readers. Staff send an order from the console to the device, the guest reviews it on a branded screen, adds a tip, and pays by tap or card reader. Every payment records straight onto the transaction.",
          bullets: [
            { label: 'Customer-facing order summary before payment',   image: `${S}/pos-app-order.png`, blurb: 'Guests see your logo, the order, and the amount due on the device before they pay, so there are no surprises at the counter.' },
            { label: 'Tip prompts with suggested amounts',             image: `${S}/pos-app-tip.png`, blurb: 'Offer three suggested tips calculated from the order, or no tip. Tips record with the payment and show up per staff member in your reports.' },
            { label: 'Tap to Pay and card reader payments',            image: `${S}/pos-app-reader.png`, blurb: 'Guests tap a card or phone on the device with Stripe or Square Tap to Pay, or pay on a Bluetooth card reader or Stripe smart reader.' },
            { label: 'PIN lock for shared devices',                    image: `${S}/pos-app-pin.png`, blurb: 'Lock the device behind a PIN, so a counter tablet stays secure between payments.' },
            { label: 'Payments recorded on the booking transaction',   image: `${S}/pos-transaction.png`, blurb: 'Card payments, tips, and the staff member who collected them appear on the same transaction as the booking. The books reconcile without a separate terminal report.' },
          ],
        },
        {
          key: 'customers',
          name: 'Customer database',
          icon: 'users',
          tagline: 'A complete record per guest',
          intro:
            "Every customer who books, signs a waiver, or makes a purchase has a profile with full history. Nine tabs covering events, transactions, payments, gift cards, waivers, surveys, photos, notes, and email activity.",
          bullets: [
            { label: 'Lifetime profile per customer, created automatically', image: `${S}/module-customers.png`, blurb: 'The first booking, waiver, or purchase creates the profile automatically. From then on, every interaction attaches to it: events, payments, waivers, surveys, photos, and email history.' },
            { label: 'Searchable, filterable list with CSV export',          image: `${S}/customers-list.png`, blurb: 'Search and filter the full customer base by activity, tags, or history, then export any slice to CSV for whatever tool comes next.' },
            { label: 'Tags, internal notes, and contact preferences',        image: `${S}/customers-tags.png`, blurb: 'Tag customers, keep internal notes, and record contact preferences. The team knows the regular, the reviewer, and the birthday group before they walk in.' },
            { label: 'One color-coded tag library for bookings and customers', image: `${S}/cust-tag-library.png`, blurb: 'Define your tags once, each with its own color, and apply them to bookings and customers alike. A tag on a booking can tag everyone in the group too.' },
            { label: 'Customer credit balances', image: `${S}/customers-credit.png`, blurb: 'Issue store credit for goodwill or rebooking, and it applies at the next checkout. Regulars come back to a balance waiting for them.' },
            { label: 'Archive without losing historical data',               image: `${S}/customers-archive.png`, blurb: 'Archive stale customers to keep working lists clean. History stays intact and searchable; nothing is deleted.' },
            { label: 'Linked across bookings, transactions, waivers, photos, surveys', image: `${S}/cust-linked.png`, blurb: 'Customer records connect to every module, so one search answers when they played, what they paid, what they signed, and what they said afterward.' },
          ],
        },
        {
          key: 'waivers',
          name: 'Waivers',
          icon: 'file',
          tagline: 'Digital signing tied to every booking',
          intro:
            "Customizable digital waivers that meet your insurer's, city's, or state's requirements. Automatically attached to bookings, stored permanently, and linked back to the customer record.",
          bullets: [
            { label: 'Brand-matched interface with RGB color and Google Fonts', image: `${S}/wv-branding.png`, blurb: 'Waivers match your brand with exact colors and fonts. The legal step feels like part of the experience instead of an interruption.' },
            { label: 'Tailored data fields for streamlined check-in',           image: `${S}/wv-fields.png`, blurb: 'Collect exactly the fields your check-in needs: emergency contacts, sizes, marketing opt-ins. Nothing extra to slow the line down.' },
            { label: 'Automatic synchronization with booking times',            image: `${S}/wv-booking-sync.png`, blurb: 'The public waiver page stays in sync with your bookings, so signers attach themselves to the right event and time without staff matching signatures to reservations.' },
            { label: 'Multi-event simultaneous check-in capability',            image: `${S}/wv-multi-event.png`, blurb: 'Check in several events at once on the same screen when two parties arrive together. Saturday rushes stay orderly.' },
            { label: 'Permanent waiver storage',                                image: `${S}/module-waivers.png`, blurb: 'Signed waivers store permanently and surface in seconds, which is exactly what your insurer wants to hear.' },
            { label: 'Intuitive filtering and reporting tools',                 image: `${S}/module-waivers.png`, blurb: 'Filter waivers by event, date, or signer to answer any question in seconds, from a liability query to a headcount.' },
            { label: 'Waivers automatically included in booking emails',        image: `${S}/wv-email-link.png`, blurb: 'Booking confirmations automatically include the waiver link, so most groups arrive already signed.' },
            { label: 'Walk-in waivers signed by QR code on guests\' phones', image: `${S}/wv-walkin-phone.png`, blurb: 'Print a QR code for the front desk and walk-ins sign on their own phones, with no booking needed. Assign a signed walk-in waiver to a booking later.' },
            { label: 'Generic waiver types alongside booking waivers',          image: `${S}/wv-generic-type.png`, blurb: 'Set up a generic waiver with its own header message and waiver text, running next to the waivers tied to your bookings.' },
          ],
        },
        {
          key: 'event-tracking',
          name: 'Event tracking & hints',
          icon: 'gamepad',
          tagline: 'Live capture, hint delivery, leaderboards',
          intro:
            "Track every session in real time. Hosts log start times, completion, hints, and notes; the data feeds analytics, leaderboards, and customer recaps. A built-in web hint system delivers text, audio, and video cues during play.",
          bullets: [
            { label: 'Track key metrics throughout the experience',          image: `${S}/landing-event-tracking.png`, blurb: 'Hosts track the session as it runs: start, objectives, hints, completion. The data flows into analytics, leaderboards, and customer recaps without extra typing.' },
            { label: 'Live pacing against past teams on every objective', image: `${S}/et-pacing.png`, blurb: 'Pacing shows how long teams usually take on each objective while the current team is still on track, so hosts can spot a slowdown and offer a hint before the team falls behind.' },
            { label: 'Monitor start times, completion, and hint usage',      image: `${S}/et-monitor.png`, blurb: 'Start times, completion rates, and hint usage are logged in the natural flow of hosting, giving you the numbers behind every session.' },
            { label: 'Segment data by group size, demographics, skill',      image: `${S}/et-segment.png`, blurb: 'Record group size and experience level with each session, then compare how newcomers, regulars, and different group sizes actually perform.' },
            { label: 'Tag sessions from the tracker',                        image: `${S}/et-tags.png`, blurb: 'Hosts tag the group right from the tracker, using the same tag library as the rest of your venue.' },
            { label: 'Web-based hints in text, audio, and video formats',    image: `${S}/et-hints.png`, blurb: 'Deliver hints to the in-room guest screen as text, audio, or video, sent by the host from one panel.' },
            { label: 'Adaptive progression support',                  image: `${S}/module-event-tracking.png`, blurb: 'Objectives can branch on what the group has already done, so hosts adapt the experience to the team in front of them.' },
            { label: 'Story videos and music cued from the tracker', image: `${S}/et-media.png`, blurb: 'Attach intro, story, and finale videos and background music to any objective, and play each one from the tracker at exactly the right beat.' },
            { label: 'Customer-shareable team stats and comparisons',        image: null, blurb: 'Teams see how they ranked against everyone else who played. The comparison is the part they screenshot and share.' },
            { label: 'Personalized post-event stats per guest',              image: null, blurb: 'Minutes after the finale, each guest can view a personalized recap of their session. The experience keeps living after they leave the building.' },
          ],
        },
        {
          key: 'photos',
          name: 'Photos',
          icon: 'camera',
          tagline: 'Capture, brand, and deliver',
          intro:
            "High-quality photos in one click. Apply branded overlays automatically with dynamic variables (event name, time, score), store at full resolution on our CDN, and send the entire batch to every guest in the group with one click.",
          bullets: [
            { label: 'Uncompressed full-resolution storage',                 image: `${S}/module-photos.png`, blurb: 'Photos store uncompressed at full resolution. What the camera captured is what the customer downloads, even years later.' },
            { label: 'Built-in template creator for branded overlays',       image: `${S}/landing-photos.png`, blurb: 'Design branded overlay templates once, and every session photo comes out framed, logoed, and ready to post.' },
            { label: 'Live camera with the overlay in frame', image: `${S}/ph-live-camera.png`, blurb: 'Frame every team photo with the overlay showing on the live camera, then review and retake before saving, so shots line up the first time.' },
            { label: 'Event-data variables in overlays',                      image: `${S}/ph-variables.png`, blurb: 'Overlays pull live event data: team name, completion time, date. Every photo comes out personalized without anyone editing anything.' },
            { label: 'Unlimited overlay options per venue',                  image: `${S}/ph-unlimited.png`, blurb: 'Create as many overlays as you have rooms, seasons, and campaigns. Halloween gets its own frame.' },
            { label: 'One-click sending to every participant',               image: `${S}/ph-send.png`, blurb: 'Send the entire batch to every participant in one click, usually before the group has left the lobby. Shared photos are marketing you did not pay for.' },
            { label: 'Long-term cloud accessibility for every customer photo', image: `${S}/ph-cloud.png`, blurb: 'Photos remain available long-term for every customer, attached to their profile rather than buried on a front-desk computer.' },
            { label: 'Direct upload to OTC, not local devices',              image: `${S}/module-photos.png`, blurb: 'Uploads go straight from the capture flow to the platform. Nothing sits on local devices waiting to be lost.' },
          ],
        },
        {
          key: 'surveys',
          name: 'Surveys',
          icon: 'message-square',
          tagline: 'Feedback you can actually act on',
          intro:
            "Send post-experience surveys automatically. Build any question type, segment by experience or host, watch trends over time. Aggregated dashboards plus question-level analytics, with lifetime response storage.",
          bullets: [
            { label: 'Unlimited customizable templates with brand-matched design', image: `${S}/sv-templates.png`, blurb: 'Build unlimited survey templates with your branding and personalized messaging, one per experience if that is what the feedback calls for.' },
            { label: 'Dropdowns, checkboxes, ratings, free text',                  image: `${S}/sv-fields.png`, blurb: 'Mix ratings, dropdowns, checkboxes, and free text to get both scores you can chart and quotes you can put on the website.' },
            { label: 'Targeted distribution by customer list, event, or date',     image: `${S}/sv-targeting.png`, blurb: 'Send surveys to a customer list, everyone from a specific event, or a date range. Feedback comes from exactly the people you meant to ask.' },
            { label: 'Automated post-event distribution',                          image: `${S}/sv-automated.png`, blurb: 'Attach a survey to your post-event automation and every group gets asked, every time, with nobody remembering to hit send.' },
            { label: 'Aggregated and per-question analytics',                      image: `${S}/module-surveys.png`, blurb: 'Aggregated dashboards show the trend; per-question views show the why. Watch a rating move after you change a puzzle.' },
            { label: 'Response-level tracking per recipient',                      image: `${S}/sv-linked.png`, blurb: 'See who responded and read each individual submission, so a bad score can turn into a recovery call the same day.' },
            { label: 'Lifetime storage of every survey response',                  image: `${S}/sv-history.png`, blurb: 'Responses store for the life of your account, building trend lines that outlast staff turnover.' },
          ],
        },
        {
          key: 'operations',
          name: 'Operations & checklists',
          icon: 'check-circle',
          tagline: 'Resets, opens, closes, accountability',
          intro:
            "Bring accountability to the unglamorous work. Digital checklists for resets, opens, closes, and reviews. Image-guided steps and per-step time tracking so you can see where the bottlenecks really are.",
          bullets: [
            { label: 'Unlimited templates with optional and required steps',  image: `${S}/op-templates.png`, blurb: 'Turn resets, opens, and closes into checklist templates with required and optional steps. The standard survives whoever happens to be on shift.' },
            { label: 'Image-guided step-by-step instructions',                image: `${S}/op-images.png`, blurb: 'Attach a reference photo to each step, so rooms reset to spec instead of to memory.' },
            { label: 'Manager review and approval of completed checklists', image: `${S}/op-approval.png`, blurb: 'Completed checklists go to a manager for review and approval, and repair needs become project tickets, so nothing flagged during a reset gets lost.' },
            { label: 'Tied to specific events for context-aware resets',      image: `${S}/op-events.png`, blurb: 'Checklists tie to specific events, so the reset for one room never gets confused with another.' },
            { label: 'Per-step time tracking and performance baselines',      image: `${S}/op-step-times.png` },
            { label: 'Permanent activity history per template',               image: `${S}/op-history.png`, blurb: 'Every completed run is kept permanently, with who ran it and when. Accountability without hovering.' },
          ],
        },
        {
          key: 'projects',
          name: 'Project management',
          icon: 'list',
          tagline: 'Tickets, kanban, time tracking',
          intro:
            "Track maintenance, improvement projects, and any operational work that needs structure. Tickets with priorities, due dates, attachments, and assignees. Subtasks, watchers, comments, and built-in time tracking.",
          bullets: [
            { label: 'Projects organized with custom prefix keys',          image: `${S}/projects-list.png`, blurb: 'Organize work into projects with custom prefix keys, so tickets read like BLDG-42 instead of a vague to-do item.' },
            { label: 'Tickets with status, priority, type, assignee',       image: `${S}/projects-ticket.png`, blurb: 'Tickets carry status, priority, type, assignee, and the full comment thread with attachments. The whole story of a repair lives in one place.' },
            { label: 'Due dates at ticket and subtask level',               image: `${S}/projects-due-dates.png`, blurb: 'Set due dates on the ticket and on each subtask, so multi-step jobs show exactly which part is slipping.' },
            { label: 'Subtasks with attachments and reordering',            image: `${S}/projects-subtasks.png`, blurb: 'Split large jobs into ordered subtasks, each with its own attachments and check-off.' },
            { label: 'Watchers, comments with attachments, audit log',      image: `${S}/projects-watchers.png`, blurb: 'Watchers get notified on changes, comments hold the discussion, and the audit log remembers all of it.' },
            { label: 'Time tracking integrated at the ticket level',        image: `${S}/projects-time.png`, blurb: 'Log time against tickets and learn what that prop repair actually cost in hours rather than guesses.' },
            { label: 'Auto-creation of booking-tied projects (optional)',   image: `${S}/projects-booking-link.png`, blurb: 'Optionally auto-create a prep ticket when qualifying bookings land, so the big corporate event arrives with its own to-do list.' },
          ],
        },
        {
          key: 'mail',
          name: 'Email & message automation',
          icon: 'mail',
          tagline: 'Emails and texts for every step of the customer journey',
          intro:
            "Create unlimited, highly customizable email templates for every part of the customer journey and your internal operations. Build them visually with a drag-and-drop editor, then use multi-step automations to control exactly what gets sent, when it gets sent, and who receives it. Want a unique email for every event? Different messaging based on group size? An internal notification when a specific booking comes in? An automatic follow-up one year after a guest visits? Text customers from the same automations and from the console, and answer every email and text conversation from one Messages page. OTC gives you the flexibility to build the communication workflows your venue actually needs.",
          bullets: [
            { label: 'Unlimited custom email templates',                     image: `${S}/mail-template-builder-2.png`, blurb: 'Build as many templates as your operation calls for: one per event, one per audience, one per season. There is no cap and no per-template cost.' },
            { label: 'Drag-and-drop visual template builder',                image: `${S}/mail-template-builder.png`, blurb: 'A drag-and-drop builder for branded email templates: blocks, images, buttons, no HTML. What you design is what lands in the inbox.' },
            { label: 'AI writing with Biku in the template builder',         image: `${S}/mail-biku-ai.png`, blurb: 'Describe the email you want and Biku writes it, or ask it to update what is already there, from rewording a heading to changing a button color.' },
            { label: 'Multi-step automations with triggers, waits, and conditions', image: `${S}/mail-automation-steps.png`, blurb: 'Chain triggers, delays, conditions, and sends into workflows that run themselves. One booking can produce a reminder, a day-after thank-you, and a review request without anyone touching it.' },
            { label: 'Personalize by event, group size, and booking data',   image: `${S}/landing-email.png`, blurb: 'Branch on the details of the booking itself, so a corporate group of twenty and a family of four receive messaging written for them rather than one generic email.' },
            { label: 'Customer-facing and internal operational emails',      image: `${S}/mail-internal.png`, blurb: 'The same engine handles guest communication and internal alerts, so a specific booking can notify your team the moment it lands.' },
            { label: 'Triggers for bookings, gift cards, tickets, and more', image: `${S}/mail-triggers.png`, blurb: 'Start automations from bookings, gift card purchases, ticket sales, and more. Each moment in the customer lifecycle can carry its own follow-up, including a check-in a full year after a visit.' },
            { label: 'Abandoned cart recovery emails', image: `${S}/mail-abandoned-cart.png`, blurb: 'Email customers who left a booking in the cart and bring them back to finish it.' },
            { label: 'Every email and text conversation on one Messages page', image: `${S}/sms-messages.png`, blurb: 'Every email and text conversation with a customer sits on one Messages page, so anyone on the team can pick up the thread and reply.' },
            { label: 'Text messages as a step in any automation',            image: `${S}/sms-automation-step.png`, blurb: 'Add a text to any automation next to your emails, so reminders and confirmations reach guests on the phone they actually check.' },
            { label: 'Reusable text templates with delivery tracking',       image: `${S}/sms-templates.png`, blurb: 'Write a text once and reuse it in every automation, then see how many each template sent and how many were delivered.' },
            { label: 'Your own local number, registered for you',            image: `${S}/sms-setup.png`, blurb: 'Text guests from a local number in your own business name. Carrier registration, opt-in and opt-out handling are built in.' },
            { label: 'Custom sending domain (DKIM and DMARC)',               image: null, blurb: 'Send from your own domain with DKIM and DMARC configured in-app, which is the difference between the inbox and the spam folder.' },
            { label: 'Built-in emails for everyday scenarios', image: `${S}/mail-simple-emails.png`, blurb: 'For the routine flow, ready-made emails cover confirmations, reminders, follow-ups, and gift card deliveries. Configure the copy once and they run themselves.' },
          ],
        },
        {
          key: 'marketing',
          name: 'Campaigns & affiliates',
          icon: 'trending',
          tagline: 'Targeted sends, attributed revenue, partner commissions',
          intro:
            "Send campaigns to exactly the customers you choose and see the revenue each one brings back. An affiliate program for local businesses, influencers, and regulars is coming soon, with tiers, booking links, promo codes, and commissions that track themselves.",
          bullets: [
            { label: 'Audience builder with a live recipient count',   image: `${S}/mkt-campaign-audience.png`, blurb: 'Filter by events played, last visit, lifetime spend, tags, and birthdays. The recipient count updates as you build, and undeliverable addresses are flagged before you send.' },
            { label: 'Review every recipient before sending',          image: `${S}/mkt-campaign-recipients.png`, blurb: 'Open the full recipient list, filter it to deliverable or undeliverable addresses, and save the audience for your next campaign.' },
            { label: 'Revenue and engagement per campaign',            image: `${S}/mkt-campaign-report.png`, blurb: 'Every campaign report puts deliveries, opens, clicks, and deliverability next to the purchases and revenue the campaign brought in.' },
            { label: 'Birthday campaigns on autopilot', image: `${S}/mkt-birthday.png`, blurb: 'Every customer gets an email before their birthday with a single use promo code of their own.' },
            { label: 'Affiliate program with commission tiers',        image: `${S}/mkt-affiliates.png`, blurb: 'Track local businesses, influencers, and players as affiliates, each with a commission tier and the amount you owe them.', soon: true },
            { label: 'Booking links and promo codes per affiliate',    image: `${S}/mkt-affiliate-overview.png`, blurb: 'Every affiliate gets a source code, a booking link, and optional promo codes, so their sales are attributed without anyone keeping a spreadsheet.', soon: true },
            { label: 'Commission ledger for every sale',               image: `${S}/mkt-affiliate-ledger.png`, blurb: 'The ledger records the commission earned on each attributed sale, and payouts are recorded against it.', soon: true },
            { label: 'Attribution on every transaction', image: `${S}/mkt-attribution.png`, blurb: 'Every order records the source, medium, and campaign behind the sale, so you know which channel earned it.' },
          ],
        },
        {
          key: 'reports',
          name: 'Reports, dashboards & analytics',
          icon: 'bar-chart',
          tagline: 'Custom reports, widget grids, leaderboards',
          intro:
            "Build custom reports with the fields you care about. Save them, run them on demand for any time range, with no caps. Build live dashboards with chart, statistic, and activity-stream widgets and put them on a venue screen. Leaderboards rank teams across events.",
          bullets: [
            { label: 'Customizable reports with your preferred data fields', image: `${S}/module-reports.png`, blurb: 'Choose the exact fields you care about, save the configuration, and rerun it on demand. The report you need monthly takes one click, not a rebuild.' },
            { label: 'Unlimited reports across any time period',             image: `${S}/rp-unlimited.png`, blurb: 'Run any report across any time period with no caps. All historical data stays accessible within seconds.' },
            { label: 'Custom dashboards with chart, stat, activity widgets', image: `${S}/landing-reports.png`, blurb: 'Compose dashboards from chart, statistic, and activity widgets, each with its own time range. Your morning numbers, arranged your way.' },
            { label: 'Revenue by traffic source and campaign', image: `${S}/rp-traffic-source.png`, blurb: 'See which channels and campaigns bring in bookings and revenue, on dashboards and as report columns.' },
            { label: 'Dashboard-wide filters across every location',        image: `${S}/rp-dashboard-filters.png`, blurb: 'Filter a whole dashboard at once by date, location, event, payment method, and more. Every widget follows the filter.' },
            { label: 'Period comparisons on any widget',                     image: `${S}/rp-compare.png`, blurb: 'Compare a period against the ones before it or the same period in past years, with the change shown at a glance.' },
            { label: 'Detail and summary views in every report',             image: `${S}/rp-summary.png`, blurb: 'Switch a report between detail rows and a summary grouped by the field you choose.' },
            { label: 'Staff reports with tips per person',                   image: `${S}/rp-staff.png`, blurb: 'See every event each staff member worked and the tips they collected, filtered by date, event, or person.' },
            { label: 'Staff utilization widgets',                            image: `${S}/rp-utilization.png`, blurb: 'Compare booked hours against available hours for each staff member, so scheduling decisions rest on numbers.' },
            { label: 'CSV export of any data table',                         image: `${S}/rp-export.png`, blurb: 'Every data table exports to CSV in one click, for the accountant, the spreadsheet, or the board deck.' },
            { label: 'Pin a dashboard for fullscreen venue display',         image: `${S}/reports-fullscreen.png`, blurb: 'Pin any dashboard to a fullscreen venue display and let the day run across the screen.' },
            { label: 'Event-specific analytics and trends',                  image: `${S}/et-host-analytics.png`, blurb: 'Analytics per experience show performance over time, so you know which room needs a refresh before the reviews tell you.' },
            { label: 'Team leaderboards with venue-screen fullscreen mode',  image: null, blurb: 'Fullscreen leaderboards rank teams across events. On a lobby screen they turn one visit into a rivalry.' },
          ],
        },
        {
          key: 'team',
          name: 'Team, roles & multi-location',
          icon: 'shield',
          tagline: 'Granular permissions, multiple groups',
          intro:
            "Add unlimited staff users at no extra cost. Granular role-based permissions with three access levels (all, own, none). Multi-location support via company groups, comprehensive activity logging, and Google Calendar / Tag Manager / Meta Pixel / GA4 integrations.",
          bullets: [
            { label: 'Unlimited user accounts at no per-seat cost',         image: `${S}/tm-users.png`, blurb: 'Add your whole staff at no per-seat cost. Everyone works under their own account, and the price stays the same.' },
            { label: 'Granular feature customization per role',             image: `${S}/module-team.png`, blurb: 'Set each feature per role to all records, own records, or none. The trainee sees their schedule; the manager sees the business.' },
            { label: 'Role-specific notification settings',                 image: `${S}/tm-notifications.png`, blurb: 'Route notifications by role, so owners hear about refunds and hosts hear about tomorrow, not the other way around.' },
            { label: 'Unlimited custom roles',                              image: `${S}/tm-roles.png`, blurb: 'Create as many roles as your venue really has, from trainee to general manager, each with its own permission set.' },
            { label: 'Comprehensive user activity logging',                 image: `${S}/tm-activity.png`, blurb: 'Comprehensive logging records who did what and when across the platform. Questions about a change get answered by the log.' },
            { label: 'Saved views on every list', image: `${S}/tm-saved-views.png`, blurb: 'Save the filters you use every day and open any of them in one click.' },
            { label: 'Biku AI assistant on every page', image: `${S}/tm-biku.png`, blurb: 'Ask Biku how to set something up or where to find it from any page, and pick the conversation up where you left off.' },
            { label: 'Multi-location support via company groups',           image: `${S}/team-groups.png`, blurb: 'Company groups give each location its own settings, staff, and data under one login, with visibility across all of them.' },
            { label: 'Open a new location by copying an existing one', image: `${S}/tm-copy-location.png`, blurb: 'Copy events, schedules, templates, and settings into a new or existing location, or merge two locations into one. A second venue is set up in an afternoon.' },
            { label: 'Staff time tracking verified at your venue', image: `${S}/tm-time-tracking.png`, blurb: 'Staff log their hours in the console, and a location check warns about or blocks entries made away from the venue, so timesheets match reality.' },
            { label: 'Integrations with GTM, Meta Pixel, GA4, Google Calendar', image: `${S}/team-integrations.png`, blurb: 'Native connections for Google Tag Manager, Meta Pixel, GA4, and Google Calendar, so ad tracking and staff calendars work without duct tape.' },
            { label: 'Webhooks for bookings, orders, and payments', image: `${S}/tm-webhooks.png`, blurb: 'Send signed requests to your own systems when bookings are created, cancelled, or rescheduled, when orders and payments change, and when an event is completed. Failed deliveries retry automatically.' },
            { label: 'API access with a full request log',                  image: `${S}/tm-api.png`, blurb: 'Connect your own tools with an API key you can roll at any time, and review every request made with it.' },
            { label: 'Public system status page',                           image: `${S}/tm-status.png`, blurb: 'A public status page reports uptime for online booking and payments, waiver check-in, the console, and the API.' },
            { label: 'Data migration tooling for historical records',       image: `${S}/team-migration.png`, blurb: 'Import tooling brings your historical bookings, customers, and waivers with you, so day one starts with your history intact.' },
          ],
        },
      ],
      activeBulletByKey: {},
      chapters: [
        { eyebrow: '01', title: 'Fill the calendar', band: false, keys: ['bookings', 'booking-site', 'payments'] },
        { eyebrow: '02', title: 'Run the day', band: true, keys: ['waivers', 'pos', 'event-tracking', 'photos', 'operations'] },
        { eyebrow: '03', title: 'Know your business', band: false, keys: ['customers', 'surveys', 'reports'] },
        { eyebrow: '04', title: 'Run the company', band: true, keys: ['projects', 'mail', 'marketing', 'team'] },
      ],
    };
  },
  computed: {
    chapterView() {
      return this.chapters.map(c => ({
        ...c,
        modules: c.keys
          .map(k => this.features.find(f => f.key === k))
          .filter(Boolean),
      }));
    },
    orderedFeatures() {
      return this.chapterView.flatMap(c => c.modules);
    },
  },
  created() {
    this.features.forEach(f => {
      const first = (f.bullets || []).find(b => b.image);
      if (first) this.$set(this.activeBulletByKey, f.key, first.label);
    });
  },
  methods: {
    interactiveBullets(feature) {
      return (feature.bullets || []).filter(b => b.image);
    },
    alsoIncludes(feature) {
      return (feature.bullets || []).filter(b => !b.image);
    },
    activeBulletLabel(feature) {
      return this.activeBulletByKey[feature.key];
    },
    setActiveByLabel(key, label) {
      this.$set(this.activeBulletByKey, key, label);
    },
    currentBullet(feature) {
      const label = this.activeBulletByKey[feature.key];
      return (feature.bullets || []).find(b => b.label === label) || null;
    },
    currentImage(feature) {
      const b = this.currentBullet(feature);
      return b ? b.image : null;
    },
    goToAnchor(anchor) {
      const el = document.getElementById(anchor);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/assets/sass/marketing-tokens';

.features-page { padding-top: 110px; padding-bottom: 80px; }

.hero-sub {
  color: $muted;
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 3rem;
}

.hero-sub-link {
  color: $primary;
  text-decoration: none;
  font-weight: 600;
  &:hover { text-decoration: underline; color: $primary; }
}

/* Jump nav */

.features-jump-nav {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
  margin: 2.5rem auto 0;
  max-width: 920px;
}

.jump-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.5rem 0.85rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  font-size: 0.82rem;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;

  .svg-icon { color: $primary; opacity: 0.85; }

  &:hover, &:focus {
    text-decoration: none;
    color: white;
    border-color: $primary-border;
    background: rgba(225, 78, 202, 0.08);
    .svg-icon { opacity: 1; }
  }
}

/* Chapters */

.feature-chapter {
  padding: 1.5rem 0 2.5rem;

  &.section-band { padding: 2.5rem 0 3rem; }
}

.chapter-head {
  text-align: center;
  padding: 2rem 0 0.5rem;

  .chapter-eyebrow {
    color: $teal;
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.22em;
    margin-bottom: 0.4rem;
  }

  .chapter-title {
    color: $muted-2;
    font-size: 1rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    margin: 0;

    &::after {
      content: '';
      display: block;
      width: 44px;
      height: 2px;
      margin: 0.9rem auto 0;
      background: $teal-border;
    }
  }
}

/* Feature sections */

.feature-section { padding: 3.25rem 0; }

.feature-header {
  max-width: 720px;
  margin: 0 auto 1.75rem;
  text-align: center;
}

.feature-icon-wrap {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: $teal-soft;
  border: 1px solid rgba(46, 201, 183, 0.25);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  color: $teal;
}

.feature-section-title {
  @extend .section-title;
  // Override section-title default margin
  margin-bottom: 0.85rem;
}

.feature-section-body {
  color: $muted;
  font-size: 1.05rem;
  line-height: 1.6;
}

/* Coming soon marker on bullets for features not open to every venue yet */
.soon-pill {
  display: inline-block;
  margin-left: 0.5rem;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  border: 1px solid $primary-border;
  color: $primary;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
  vertical-align: middle;
}

/* Bullets */

.feature-bullet-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.feature-bullet {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 0.85rem 1rem;
  margin-bottom: 0.4rem;
  border-radius: 8px;
  background: $card-bg;
  border: 1px solid rgba(255, 255, 255, 0.05);
  color: $muted;
  font-size: 0.95rem;
  line-height: 1.4;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, transform 0.15s ease;

  ::v-deep .svg-icon {
    color: $decorative;
    margin-top: 0.15rem;
    flex-shrink: 0;
    transition: color 0.15s ease;
  }

  &:hover {
    border-color: rgba(225, 78, 202, 0.3);
    color: white;
  }

  &.active {
    border-color: $primary-border;
    background: rgba(225, 78, 202, 0.1);
    color: white;
    transform: translateX(3px);
    ::v-deep .svg-icon { color: $primary; }
  }
}

/* Also includes (bullets without screenshots) */

.feature-also-includes {
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);

  h4 {
    color: $muted-2;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    margin-bottom: 0.75rem;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  li {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.3rem 0;
    color: $muted;
    font-size: 0.88rem;
    line-height: 1.4;

    ::v-deep .svg-icon {
      color: $teal;
      margin-top: 0.2rem;
      flex-shrink: 0;
    }
  }
}

/* Screenshot stage */

.screenshot-stage {
  position: sticky;
  top: 100px;
  background: $card-bg;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  padding: 1.25rem;
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.35);

  @media (max-width: 768px) {
    position: static;
    margin-top: 1.5rem;
  }
}

.screenshot-image {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.2);
  min-height: 240px;
  object-fit: contain;
}

.screenshot-caption {
  margin: 0.85rem auto 0;
  max-width: 520px;
  text-align: center;
}

.screenshot-caption-title {
  margin: 0;
  color: rgba(255, 255, 255, 0.78);
  font-size: 0.82rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.screenshot-caption-blurb {
  margin: 0.35rem 0 0;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.78rem;
  line-height: 1.55;
}

/* Crossfade transition between screenshots */
.screenshot-fade-enter-active,
.screenshot-fade-leave-active {
  transition: opacity 0.2s ease;
}
.screenshot-fade-enter,
.screenshot-fade-leave-to {
  opacity: 0;
}

/* Mobile tuning */

@media (max-width: 768px) {
  .features-page { padding-top: 90px; }
  .hero-sub { font-size: 1rem; margin-bottom: 1.5rem; }
  .feature-section { padding: 2.25rem 0; }
  .feature-chapter { padding: 0.75rem 0 1.5rem; }
  .feature-chapter.section-band { padding: 1.5rem 0 2rem; }
}
</style>
